import React, { useState } from 'react';
import { useApp } from '../../context/AppContext.tsx';
import {
  Briefcase,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Images,
  Play,
  Film,
} from 'lucide-react';

interface PortfolioGridProps {
  badge?: string;
  title?: string;
  subtitle?: string;
  buttonText?: string;
  buttonEnabled?: boolean;
  limit?: number;
  isPageMode?: boolean;
  layoutStyle?: 'default' | 'compact' | 'wide' | 'cards' | 'featured_highlight';
  sectionId?: string;
  selectedPortfolioId?: string;
}

export default function PortfolioGrid({
  title = 'Profil Project & Rekam Jejak Unggulan',
  subtitle = 'Dokumentasi implementasi proyek nyata, konsultasi keberlanjutan, dan hasil kerja sama strategis dengan mitra.',
  sectionId = 'sec-portfolio',
  selectedPortfolioId,
}: PortfolioGridProps) {
  const { portfolio, navigate } = useApp();
  const [activeMediaIndex, setActiveMediaIndex] = useState<number>(0);

  const itemIndex = sectionId && (sectionId.includes('-2') || sectionId.includes('2')) ? 1 : 0;
  const singleItem =
    (selectedPortfolioId ? portfolio?.find((p) => p.id === selectedPortfolioId) : null) ||
    (portfolio && portfolio.length > itemIndex ? portfolio[itemIndex] : (portfolio && portfolio.length > 0 ? portfolio[0] : null));

  // Video & Media Helpers
  const getYouTubeId = (url?: string) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/);
    return match ? match[1] : null;
  };

  const extractIframeSrc = (code?: string) => {
    if (!code) return '';
    const match = code.match(/src=["']([^"']+)["']/i);
    return match ? match[1] : '';
  };

  const isDirectVideo = (url?: string) =>
    Boolean(url) && (/\.(mp4|webm|mov|mkv)(\?.*)?$/i.test(url) || url.includes('/uploads/'));

  interface MediaItem {
    type: 'photo' | 'video';
    url: string;
    videoEmbed?: string;
  }

  const singleMediaItems: MediaItem[] = [];
  if (singleItem) {
    if (singleItem.videoUrl || singleItem.videoEmbed) {
      singleMediaItems.push({
        type: 'video',
        url: singleItem.videoUrl || '',
        videoEmbed: singleItem.videoEmbed,
      });
    }
    const photos = [singleItem.coverImage, ...(singleItem.gallery || [])].filter(Boolean);
    photos.forEach((img) => {
      if (img !== singleItem.videoUrl) {
        singleMediaItems.push({ type: 'photo', url: img });
      }
    });
  }

  const currentSingleMedia = singleMediaItems[activeMediaIndex] || singleMediaItems[0];

  const handleNextMedia = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (singleMediaItems.length <= 1) return;
    setActiveMediaIndex((prev) => (prev === singleMediaItems.length - 1 ? 0 : prev + 1));
  };

  const handlePrevMedia = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (singleMediaItems.length <= 1) return;
    setActiveMediaIndex((prev) => (prev === 0 ? singleMediaItems.length - 1 : prev - 1));
  };

  // If no portfolio items yet
  if (!portfolio || portfolio.length === 0 || !singleItem) {
    return (
      <section id="portfolio-grid-section" className="py-10 sm:py-16 transition-colors">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 text-center">
          <div className="p-8 rounded-3xl bg-slate-50 border border-slate-200/80 max-w-lg mx-auto shadow-2xs">
            <Briefcase className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
            <h3 className="font-extrabold text-base text-slate-900">{title}</h3>
            <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
              Profil Project belum ditambahkan. Masuk ke Dashboard Admin untuk mengunggah proyek utama, foto slider, dan video.
            </p>
          </div>
        </div>
      </section>
    );
  }

  const displayTitle = singleItem.title || title;
  const displaySubtitle = singleItem.summary || subtitle;

  return (
    <section id="portfolio-grid-section" className="py-8 sm:py-16 transition-colors">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        
        {/* Grid Title & Subtitle Header - Aligned cleanly with other grid headers */}
        <div className="mb-6 sm:mb-10 max-w-3xl">
          <h2 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight leading-tight">
            {displayTitle}
          </h2>

          {displaySubtitle && (
            <p className="text-sm text-slate-600 mt-2 leading-relaxed font-normal">
              {displaySubtitle}
            </p>
          )}
        </div>

        {/* Single Dedicated Project Showcase Layout (Unboxed, Modern, Focused) */}
        <article
          onClick={() => navigate('portfolio-detail', singleItem.slug || singleItem.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate('portfolio-detail', singleItem.slug || singleItem.id);
            }
          }}
          tabIndex={0}
          role="button"
          aria-label={`Buka profil lengkap project ${singleItem.title}`}
          className="group transition-all duration-300 cursor-pointer outline-hidden focus-visible:ring-2 focus-visible:ring-emerald-500"
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-10 lg:gap-12 items-start">
            
            {/* Left Photo/Video & Gallery Slider Media (lg:col-span-7 - Enlarged Media) */}
            <div className="lg:col-span-7 space-y-3">
              <div className="relative aspect-16/10 sm:aspect-16/9 rounded-2xl sm:rounded-3xl overflow-hidden bg-slate-950 shadow-md group-hover:shadow-xl transition-all">
                {currentSingleMedia?.type === 'video' ? (
                  isDirectVideo(currentSingleMedia.url) ? (
                    <video
                      src={currentSingleMedia.url}
                      controls
                      playsInline
                      className="w-full h-full object-contain"
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : currentSingleMedia.videoEmbed && currentSingleMedia.videoEmbed.includes('<iframe') ? (
                    <iframe
                      src={extractIframeSrc(currentSingleMedia.videoEmbed) || currentSingleMedia.url}
                      title={singleItem.title}
                      className="w-full h-full border-0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : getYouTubeId(currentSingleMedia.url) ? (
                    <iframe
                      src={`https://www.youtube.com/embed/${getYouTubeId(currentSingleMedia.url)}`}
                      title={singleItem.title}
                      className="w-full h-full border-0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <iframe
                      src={currentSingleMedia.url}
                      title={singleItem.title}
                      className="w-full h-full border-0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      onClick={(e) => e.stopPropagation()}
                    />
                  )
                ) : (
                  <img
                    src={
                      currentSingleMedia?.url ||
                      singleItem.coverImage ||
                      'https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&w=1200&q=80'
                    }
                    alt={singleItem.title}
                    className="w-full h-full object-cover group-hover:scale-102 transition-transform duration-500"
                  />
                )}

                {/* Slide Navigation Arrows */}
                {singleMediaItems.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={handlePrevMedia}
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 hover:bg-black/85 text-white flex items-center justify-center shadow-lg transition-all cursor-pointer z-10 border border-white/20 active:scale-95"
                      aria-label="Media Sebelumnya"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      type="button"
                      onClick={handleNextMedia}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 hover:bg-black/85 text-white flex items-center justify-center shadow-lg transition-all cursor-pointer z-10 border border-white/20 active:scale-95"
                      aria-label="Media Selanjutnya"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </>
                )}

                {/* Media Counter Badge */}
                {singleMediaItems.length > 1 && (
                  <div className="absolute bottom-3 right-3 bg-black/80 backdrop-blur-xs text-white text-xs font-bold px-3 py-1.5 rounded-xl flex items-center gap-2 border border-white/20 shadow-md select-none">
                    {currentSingleMedia?.type === 'video' ? (
                      <Film className="w-3.5 h-3.5 text-purple-400" />
                    ) : (
                      <Images className="w-3.5 h-3.5 text-emerald-400" />
                    )}
                    <span>
                      {activeMediaIndex + 1} / {singleMediaItems.length} Media
                    </span>
                  </div>
                )}
              </div>

              {/* Gallery Thumbnail Bar */}
              {singleMediaItems.length > 1 && (
                <div
                  className="flex items-center gap-2.5 overflow-x-auto pb-1 scrollbar-none"
                  onClick={(e) => e.stopPropagation()}
                >
                  {singleMediaItems.map((item, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setActiveMediaIndex(idx)}
                      className={`relative w-20 h-14 rounded-xl overflow-hidden border-2 transition-all shrink-0 cursor-pointer bg-slate-900 ${
                        activeMediaIndex === idx
                          ? 'border-emerald-600 scale-105 shadow-sm ring-2 ring-emerald-500/30'
                          : 'border-transparent opacity-65 hover:opacity-100'
                      }`}
                      title={`Lihat media ${idx + 1}`}
                    >
                      {item.type === 'video' ? (
                        <div className="w-full h-full flex items-center justify-center bg-purple-950/90 text-white relative">
                          <Play className="w-4 h-4 fill-current text-purple-300" />
                        </div>
                      ) : (
                        <img src={item.url} alt={`Gallery ${idx + 1}`} className="w-full h-full object-cover" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Right Text & Project Narrative (Directly beside photo) */}
            <div className="lg:col-span-5 flex flex-col justify-between space-y-4 pt-1">
              <div className="space-y-4">
                {/* Story Narrative Text */}
                {singleItem.description ? (
                  <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-line max-h-80 overflow-y-auto pr-1">
                    {singleItem.description}
                  </div>
                ) : singleItem.summary ? (
                  <p className="text-sm text-slate-700 leading-relaxed">
                    {singleItem.summary}
                  </p>
                ) : null}

                {/* Highlights / Key Outcomes */}
                {singleItem.highlights && singleItem.highlights.length > 0 && (
                  <div className="pt-2 space-y-2">
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block">
                      Poin Capaian Utama:
                    </span>
                    <div className="space-y-2">
                      {singleItem.highlights.map((h, i) => (
                        <div
                          key={i}
                          className="flex items-start gap-2.5 text-xs font-semibold text-slate-800"
                        >
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                          <span>{h}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 'Selengkapnya' Action Link right below text */}
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('portfolio-detail', singleItem.slug || singleItem.id);
                    }}
                    className="inline-flex items-center gap-2 text-xs sm:text-sm font-black text-emerald-700 hover:text-emerald-800 transition-colors cursor-pointer group"
                  >
                    <span>Selengkapnya</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            </div>

          </div>
        </article>

      </div>
    </section>
  );
}
